/**
 * @fileoverview Input/output schemas for the generate-world-element-portrait-v2 composite agent.
 *
 * This composite orchestrates prompt generation followed by image generation
 * for world elements (place, set, object, species). The composite runs both
 * steps sequentially on the server without a user review point.
 */

import { z } from 'zod';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Input for the composite flow.
 *
 * Composites carry only the orchestration-log metadata (logId, model,
 * projectId). They have no customPrompt — children create their own input
 * bags with their own logIds and prompts.
 */
export const InputSchema = z.object({
  logId: z.string(),
  model: z.string(),
  projectId: z.string().optional(),

  // Element context
  elementName: z.string().describe('The name of the world element.'),
  elementType: z.enum(['place', 'set', 'object', 'species']).describe('The world element type.'),
  elementSubtype: z.string().describe("The element subtype (e.g., 'location', 'building', 'artifact', 'creature')."),
  elementDetails: z.record(z.string()).describe("A key-value object of details from the element's latest snapshot or initial description."),

  // Story context
  storySetting: z.string().optional().describe("A generic real-world setting for context (e.g., 'Medieval Europe')."),
  userMessage: z.string().optional().describe('Optional user guidance for the prompt generation.'),

  // FAL image generation options
  overrideModel: z.string().optional().describe('Override the FAL model for image generation.'),
  aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).optional().describe('Normalized aspect ratio.'),
  quality: z.enum(['draft', 'standard', 'high']).optional().describe('Quality level.'),
  enableSafetyChecker: z.boolean().optional().default(false).describe('Enable or disable the FAL safety checker.'),
  numberOfImages: z.number().min(1).max(4).optional().describe('Number of images to generate (1-4).'),
  outputFormat: z.enum(['png', 'jpeg', 'webp']).optional().describe('Output image format.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  imagePrompt: z.string().describe('The generated image prompt that was used for generation.'),
  imageDataUri: z.string().describe('The generated image, returned as a Base64-encoded data URI.'),
});

export type Output = z.infer<typeof OutputSchema>;
